import { Button } from '@/components/Button';
import useProvidedJobPosts from '../../provided_job_posts/hooks/useProvidedJobPosts';
import ProvidedJobPostCard from '../../provided_job_posts/components/ProvidedJobPostCard';
import SelectionBar from '../../provided_job_posts/components/SelectionBar';
import useBulkShareAndSuggest from '../../provided_job_posts/hooks/useBulkShareAndSuggest';
import { MdAdd } from 'react-icons/md';
import Select from '@/components/Select';
import { COMPANY_JOB_POST_ENDPOINTS_SORT_ITEMS } from '../constants';
import Checkbox from '@/components/Checkbox';
import Loading from '@/components/Loading';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type { AgentApiProvidedJobPostsIndex } from '@/types/serializers';
import { Link } from '@tanstack/react-router';
import {
  createManualSelection,
  selectionCount,
  toggleSelection,
  type Selection,
} from '../../provided_job_posts/hooks/useSelection';

type Props = {
  companyId: number;
};

export default function CompaniesJobsCard({ companyId }: Props) {
  const [sort, setSort] = useState('updated_at:desc');
  const [isArchived, setIsArchived] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<Selection>(createManualSelection());

  const searchQuery = useMemo(
    () => ({
      company_ids: [companyId],
      custom_tag_ids: [],
      list_ids: [],
      is_include_archived: isArchived,
      sort,
      status: [],
    }),
    [companyId, sort, isArchived]
  );

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProvidedJobPosts(searchQuery);

  const providedJobPosts = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page: AgentApiProvidedJobPostsIndex) => page.records);
  }, [data?.pages]);

  const {
    handleWebShare,
    handlePdfShare,
    handleSuggestToCandidates,
    isProcessing,
    pdfProgress,
    dialogs,
  } = useBulkShareAndSuggest({
    providedJobPosts,
    selection,
    searchQuery,
  });

  const handleScroll = useCallback(
    (e: Event) => {
      const target = e.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (isFetchingNextPage || !hasNextPage) {
        return;
      }

      if (scrollTop + clientHeight >= scrollHeight - 500) {
        fetchNextPage();
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const handleCheckedChange = useCallback((id: number, checked: boolean) => {
    setSelection((prev) => toggleSelection(prev, id, checked));
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelection(createManualSelection());
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = (e: Event) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => handleScroll(e), 100);
    };

    const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;

      const computedStyle = window.getComputedStyle(element);
      const isScrollable =
        computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll';

      if (isScrollable && element.scrollHeight > element.clientHeight) {
        return element;
      }

      return findScrollContainer(element.parentElement);
    };

    const scrollContainer = containerRef.current ? findScrollContainer(containerRef.current) : null;

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', debouncedScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', debouncedScroll);
        clearTimeout(timeoutId);
      };
    } else {
      window.addEventListener('scroll', debouncedScroll);
      return () => {
        window.removeEventListener('scroll', debouncedScroll);
        clearTimeout(timeoutId);
      };
    }
  }, [handleScroll]);

  return (
    <div className="flex min-h-full flex-col">
      <div ref={containerRef} className="flex flex-1 flex-col p-lg">
        <div className="flex items-center">
          <div className="mr-md flex w-[196px] shrink-0 flex-col gap-2">
            <Select
              className="w-full"
              items={COMPANY_JOB_POST_ENDPOINTS_SORT_ITEMS}
              value={sort}
              onValueChange={(value) => setSort(value)}
            />
          </div>
          <Checkbox checked={isArchived} onChange={(checked) => setIsArchived(checked)}>
            <span className="text-sm font-normal">アーカイブされた求人を含む</span>
          </Checkbox>
          <div className="ml-auto">
            <Button type="button" asChild>
              <Link
                to="/agent/provided_job_posts/new/manual"
                className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 no-underline hover:bg-gray-50 hover:text-gray-700"
              >
                <MdAdd className="size-icon-sm" />
                求人を追加する
              </Link>
            </Button>
          </div>
        </div>
        <div className="mt-3">
          {isLoading ? (
            <div className="flex h-[200px] items-center justify-center">
              <p>読み込み中...</p>
            </div>
          ) : providedJobPosts.length === 0 ? (
            <div className="flex h-[200px] items-center justify-center">
              <p className="text-center text-sm text-placeholder">求人がまだ登録されていません</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {providedJobPosts.map((job) => (
                <ProvidedJobPostCard
                  key={job.id}
                  {...job}
                  isChecked={selection.mode === 'manual' && selection.ids.has(job.id)}
                  onCheckedChange={(checked) => handleCheckedChange(job.id, checked)}
                />
              ))}
            </div>
          )}
          {isFetchingNextPage && (
            <div className="flex justify-center py-md">
              <Loading size="small" />
            </div>
          )}
        </div>
      </div>
      <SelectionBar
        selectedCount={selectionCount(selection)}
        isProcessing={isProcessing}
        pdfProgress={pdfProgress}
        onDeselectAll={handleDeselectAll}
        onWebShare={handleWebShare}
        onPdfShare={handlePdfShare}
        onSuggestToCandidates={handleSuggestToCandidates}
      />
      {dialogs}
    </div>
  );
}
